import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import useMount from 'react-use/lib/useMount';
import Typography from '@material-ui/core/Typography';
import Button from '@arcblock/ux/lib/ActionButton';

import { withPrefix } from 'gatsby';

import Paginator from './paginator';
import TableOfContents from './toc';

import { ReactComponent as GitterLogo } from '../../assets/logos/gitter.svg';
import { ReactComponent as GithubLogo } from '../../assets/logos/github.svg';

import 'github-markdown-css/github-markdown.css';

export default function PageContent(props) {
  const contentRef = useRef(null);
  const [imagesToLoad, setImagesToLoad] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(0);

  function onImageLoaded() {
    setImagesLoaded(prevImagesLoaded => prevImagesLoaded + 1);
  }

  useMount(() => {
    if (props.hash) {
      // turn numbers at the beginning of the hash to unicode
      // see https://stackoverflow.com/a/20306237/8190832
      const hash = props.hash.toLowerCase().replace(/^#(\d)/, '#\\3$1 ');
      const hashElement = contentRef.current.querySelector(hash);
      if (hashElement) {
        hashElement.scrollIntoView();
      }
    }

    let toLoad = 0;
    const images = contentRef.current.querySelectorAll('img');
    images.forEach(image => {
      if (!image.complete) {
        image.addEventListener('load', onImageLoaded);
        toLoad++;
      }
    });

    setImagesToLoad(toLoad);
  });

  const pageIndex = props.pages.findIndex(page => {
    const prefixedPath = withPrefix(page.path);
    return prefixedPath === props.pathname || prefixedPath.replace(/\/$/, '') === props.pathname;
  });

  return (
    <Div>
      <MainContent>
        <PostContent
          component="div"
          ref={contentRef}
          className="content-wrapper markdown-body post-content">
          {props.children}
        </PostContent>
        <Paginator prevPage={props.pages[pageIndex - 1]} nextPage={props.pages[pageIndex + 1]} />
      </MainContent>
      <Aside>
        {props.headings.length > 0 && (
          <React.Fragment>
            <Typography component="h4" variant="h6" className="aside-heading">
              Table Of Contents
            </Typography>
            <TableOfContents
              headings={props.headings}
              mainRef={props.mainRef}
              contentRef={contentRef}
              imagesLoaded={imagesLoaded === imagesToLoad}
            />
          </React.Fragment>
        )}
        <Button href={props.githubUrl} component="a" size="small" target="_blank">
          <GithubLogo /> Edit on GitHub
        </Button>
        <Button component="a" size="small" href={props.communityPath}>
          <GitterLogo /> Community Discuss
        </Button>
      </Aside>
    </Div>
  );
}

PageContent.propTypes = {
  children: PropTypes.node.isRequired,
  pathname: PropTypes.string.isRequired,
  githubUrl: PropTypes.string.isRequired,
  pages: PropTypes.array.isRequired,
  hash: PropTypes.string.isRequired,
  mainRef: PropTypes.object.isRequired,
  headings: PropTypes.array.isRequired,
  communityPath: PropTypes.string,
};

PageContent.defaultProps = {
  communityPath: '',
};

const Div = styled.div`
  display: flex;
  align-items: flex-start;
`;

const MainContent = styled.main`
  flex-grow: 1;
  width: 0;
  max-width: 1020px;

  .markdown-body .highlight pre,
  .markdown-body pre {
    background-color: #222;
    border-radius: 5px;
  }
  .markdown-body code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, Courier New, monospace !important;
  }
  .markdown-body pre code {
    color: #fff;
    font-size: 14px;
    font-family: source-code-pro, Menlo, Monaco, Consolas, Courier New, monospace !important;
  }

  .markdown-body h1,
  .markdown-body h2,
  .markdown-body h3,
  .markdown-body h4,
  .markdown-body h5,
  .markdown-body h6 {
    font-weight: 600;
    line-height: 1.25;
    margin-bottom: 20px;
    margin-top: 40px;
  }

  .markdown-body .CodeMirror pre {
    background: #f6f8fa !important;
  }
`;

const PostContent = styled(Typography)`
  width: 100%;
  word-wrap: break-word;
  word-break: break-word;
  line-height: 1.5em;

  .alert-content {
    max-width: 100%;
    p:last-of-type {
      margin-bottom: 0;
    }
  }

  iframe {
    width: 100% !important;
  }

  a {
    color: ${props => props.theme.colors.blue};
  }
`;

const Aside = styled.aside`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 360px;
  max-height: calc(100vh - 64px);
  margin-top: -40px;
  margin-left: auto;
  padding: 40px 56px;
  padding-right: 0;
  position: sticky;
  top: 64px;

  .aside-heading {
    font-weight: 500;
  }

  .action-button {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    color: inherit;
    text-decoration: none;
    color: #404040;
    margin-bottom: 24px;
    height: 36px;
    max-width: 240px;

    svg {
      width: 42px;
      height: 42px;
      margin-right: 8px;
      fill: #404040;
      transform: scale(0.5);
    }
  }

  @media screen and (max-width: ${props => props.theme.breakpoints.values.lg}px) {
    display: none;
  }

  @media screen and (max-width: ${props => props.theme.breakpoints.values.md}px) {
    display: block;
  }

  @media screen and (max-width: ${props => props.theme.breakpoints.values.md}px) {
    display: none;
  }
`;
